<template>
    <div class="md-layout">
        <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
            <div class="custom-toolbar">
                <div class="custom-toolbar-start">
                    <router-link
                        :to="{
                            name: 'Service Areas'
                        }"
                    >
                        <h3 class="title">Service Areas</h3>
                    </router-link>
                    <h3 class="title override-day-crumb-title">
                        &nbsp;&nbsp; > &nbsp;&nbsp; {{ overrideDayData.serviceAreaName }} &nbsp;&nbsp; > &nbsp;&nbsp;
                        Override Days
                    </h3>
                </div>
            </div>
        </div>
        <div class="md-layout-item md-size-100">
            <md-card>
                <md-card-header class="md-card-header-icon md-card-header-black">
                    <div class="card-icon">
                        <md-icon>my_location</md-icon>
                    </div>
                </md-card-header>
                <md-card-content>
                    <div class="override-day-page-title">
                        Override Days{{
                            isEditOverrideDayMode && selectedOverrideDay !== undefined && selectedOverrideDay !== null
                                ? ` > ${formatDate(selectedOverrideDay.overrideDate)}`
                                : ''
                        }}
                    </div>
                    <div>
                        Override your available delivery windows for specific dates, for example Christmas Eve you may
                        have extra delivery windows.
                    </div>
                    <service-areas-override-day
                        v-if="!isEditOverrideDayMode"
                        :service-area-id="serviceAreaId"
                        :override-day-data="overrideDayData"
                        @editOverrideDay="handleEditOverrideDay"
                    />
                    <service-areas-override-day-window
                        v-if="isEditOverrideDayMode"
                        :service-area-id="serviceAreaId"
                        :override-day-window-data="selectedOverrideDay"
                    />
                    <div class="custom-toolbar custom-override-day-toolbar">
                        <div class="custom-toolbar-start">
                            <md-button
                                class="md-button md-primary custom-back-button"
                                @click="handleBackToServiceAreas"
                            >
                                {{ isEditOverrideDayMode ? 'Back to override dates' : 'Back' }}
                            </md-button>
                        </div>
                        <div class="custom-toolbar-end">
                            <md-button class="md-button md-primary custom-save-button" @click="handleSaveOverrideDays">
                                Save
                            </md-button>
                        </div>
                    </div>
                </md-card-content>
            </md-card>
        </div>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage, rowStatus } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import ServiceAreasOverrideDay from './ServiceAreasOverrideDay';
import ServiceAreasOverrideDayWindow from './ServiceAreasOverrideDayWindow';

export default {
    name: 'ServiceAreasOverrideDayPage',
    mixins: [GeneralMixin],
    components: {
        ServiceAreasOverrideDay,
        ServiceAreasOverrideDayWindow
    },
    data() {
        return {
            serviceAreaId: null,
            overrideDayData: {
                serviceAreaName: null,
                overrideDays: []
            },
            previousOverrideDays: null,
            rowStateFields: ['overrideDate', 'overrideDayName'],
            rowStateWindowFields: [
                'startTime',
                'endTime',
                'maxStops',
                'maxVolume',
                'maxWeight',
                'maxQuantity',
                'cutOffType',
                'cutOffTimeBeforeSlotValue',
                'cutOffTimeBeforeSlotType',
                'cutOffByTimeValue',
                'cutOffByTimeType',
                'cutOffByTimeNDaysPriorValue'
            ],
            isDataChanged: false,
            isEditOverrideDayMode: false,
            selectedOverrideDay: null,
            isDataUpdating: false
        };
    },
    async mounted() {
        this.serviceAreaId = this.$route.params.serviceAreaId;
        await this.getOverrideDays();
    },
    watch: {
        'overrideDayData.overrideDays': {
            deep: true,
            handler(newOverrideDays) {
                if (this.isDataUpdating || JSON.stringify(newOverrideDays) === this.previousOverrideDays) {
                    return;
                }

                const previousOverrideDays = JSON.parse(this.previousOverrideDays || '[]');

                newOverrideDays.forEach((overrideDay) => {
                    const previousOverrideDay = previousOverrideDays.find(
                        (x) => x.previousOverrideDate === overrideDay.previousOverrideDate
                    );

                    if (previousOverrideDay) {
                        const rowChanged = this.rowStateFields.some(
                            (field) => overrideDay[field] !== previousOverrideDay[field]
                        );
                        if (rowChanged && ![rowStatus.DELETE, rowStatus.ADD].includes(overrideDay.rowStatus)) {
                            this.isDataChanged = true;
                            overrideDay.rowStatus = rowStatus.EDIT;
                        }
                    }

                    if ([rowStatus.DELETE, rowStatus.ADD].includes(overrideDay.rowStatus) && !this.isDataChanged) {
                        this.isDataChanged = true;
                    }

                    const currentOverrideDayWindows = Array.isArray(overrideDay.overrideDayWindows)
                        ? overrideDay.overrideDayWindows
                        : [];

                    currentOverrideDayWindows.forEach((overrideDayWindow) => {
                        if (
                            overrideDayWindow.overrideDayWindowId !== null &&
                            ![rowStatus.DELETE, rowStatus.ADD].includes(overrideDayWindow.rowStatus)
                        ) {
                            const previousOverrideDayWindow = previousOverrideDay?.overrideDayWindows?.find(
                                (x) => x.overrideDayWindowId === overrideDayWindow.overrideDayWindowId
                            );

                            if (previousOverrideDayWindow) {
                                const windowRowChanged = this.rowStateWindowFields.some(
                                    (field) => overrideDayWindow[field] !== previousOverrideDayWindow[field]
                                );
                                if (windowRowChanged) {
                                    this.isDataChanged = true;
                                    overrideDayWindow.rowStatus = rowStatus.EDIT;
                                }
                            }
                        }

                        if (
                            [rowStatus.DELETE, rowStatus.ADD].includes(overrideDayWindow.rowStatus) &&
                            !this.isDataChanged
                        ) {
                            this.isDataChanged = true;
                        }
                    });
                });

                this.previousOverrideDays = JSON.stringify(this.overrideDayData.overrideDays);
            }
        }
    },
    methods: {
        async getOverrideDays() {
            this.$_handleLoaderState(true);
            try {
                const api = `/api/teams/service-areas/${this.serviceAreaId}/override-days`;
                const response = await handleRequests(api);
                this.isDataUpdating = true;
                this.overrideDayData = {
                    serviceAreaName: null,
                    overrideDays: []
                };
                await this.$nextTick(() => {
                    this.overrideDayData = response.data;
                    this.previousOverrideDays = JSON.stringify(this.overrideDayData.overrideDays);
                    this.isDataUpdating = false;
                });
                this.isDataChanged = false;
                this.resetToMainPage();
                this.$_handleLoaderState(false);
            } catch (e) {
                this.$_handleLoaderState(false);
                const message = 'Cannot retrieve override days.';
                showErrorMessage(this, message, e);
            }
        },
        async getOverrideDayWindows(overrideDay) {
            this.$_handleLoaderState(true);
            try {
                const api = `/api/teams/service-areas/${this.serviceAreaId}/override-day-windows/${
                    overrideDay.previousOverrideDate
                }`;
                const response = await handleRequests(api);
                this.isDataUpdating = true;
                overrideDay.overrideDayWindows = [];
                await this.$nextTick(() => {
                    overrideDay.overrideDayWindows = response.data.overrideDayWindows;
                    this.previousOverrideDays = JSON.stringify(this.overrideDayData.overrideDays);
                    this.isDataUpdating = false;
                });
                this.selectedOverrideDay = overrideDay;
                this.isEditOverrideDayMode = true;
                this.$_handleLoaderState(false);
            } catch (e) {
                this.$_handleLoaderState(false);
                const message = 'Cannot retrieve override days.';
                showErrorMessage(this, message, e);
            }
        },
        hasOverrideDateDuplicates() {
            const { overrideDays } = this.overrideDayData;
            const filteredOverrideDays = overrideDays.filter((x) => x.rowStatus !== rowStatus.DELETE);
            const hasDuplicates = filteredOverrideDays
                .map((x) => x.overrideDate)
                .some((value, index, self) => self.indexOf(value) !== index);

            return hasDuplicates;
        },
        overrideDayDataIsValid() {
            const { overrideDays } = this.overrideDayData;
            const filteredOverrideDays = overrideDays.filter((x) => x.rowStatus !== rowStatus.DELETE);
            const missingFields = [];
            const invalidFields = [];

            if (filteredOverrideDays.some((x) => x.overrideDayName === null || x.overrideDayName === '')) {
                missingFields.push('Name');
            }

            if (filteredOverrideDays.some((x) => x.overrideDate === null)) {
                missingFields.push('Override Date');
            }

            const filteredOverrideDayWindows = filteredOverrideDays
                .filter((x) => x.overrideDayWindows !== null)
                .map((x) => x.overrideDayWindows)
                .flat()
                .filter((x) => x.rowStatus !== rowStatus.DELETE);

            if (filteredOverrideDayWindows.some((x) => x.startTime == null)) {
                missingFields.push('From Time');
            }

            if (filteredOverrideDayWindows.some((x) => x.endTime == null)) {
                missingFields.push('To Time');
            }

            if (filteredOverrideDayWindows.some((x) => !this.$_isValid24HourTime(x.startTime))) {
                invalidFields.push('From Time format');
            }

            if (filteredOverrideDayWindows.some((x) => !this.$_isValid24HourTime(x.endTime))) {
                invalidFields.push('To Time format');
            }

            if (filteredOverrideDayWindows.some((x) => !this.$_isValid24HourTimeRange(x.startTime, x.endTime))) {
                invalidFields.push('From and To Time range');
            }

            if (missingFields.length > 0) {
                showErrorMessage(
                    this,
                    `${missingFields.join(', ')} ${missingFields.length === 1 ? 'is' : 'are'} required.`
                );
                return false;
            }

            if (invalidFields.length > 0) {
                showErrorMessage(
                    this,
                    `${invalidFields.join(', ')} ${invalidFields.length === 1 ? 'is' : 'are'} invalid.`
                );
                return false;
            }

            if (this.hasOverrideDateDuplicates()) {
                showErrorMessage(this, 'Duplicate Override Date found.');
                return false;
            }

            return true;
        },
        async saveOverrideDays(onSaveSuccess) {
            this.$_handleLoaderState(true, 'SAVING...');
            const payload = {
                method: 'post',
                data: {
                    serviceAreaId: parseInt(this.serviceAreaId, 10),
                    overrideDays: this.getChangedRows()
                }
            };
            const api = '/api/teams/service-areas/override-days';
            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Override days saved!',
                    type: 'success'
                });
                this.isDataChanged = false;
                await this.getOverrideDays();
                this.isEditOverrideDayMode = false;
            } catch (e) {
                this.$_handleLoaderState(false);
                const message = 'Cannot save override days.';
                showErrorMessage(this, message, e);
            }
        },
        resetToMainPage() {
            if (this.selectedOverrideDay) {
                this.selectedOverrideDay.overrideDayWindows = null;
                this.selectedOverrideDay = null;
            }
        },
        checkForUnsavedChanges() {
            return new Promise((resolve, reject) => {
                if (this.isDataChanged) {
                    this.$messageBox
                        .show({
                            class: 'sm-modal-container',
                            title: 'Save override days',
                            body: 'You have unsaved changes. Would you like to save the override days?',
                            buttons: ['Yes', 'No']
                        })
                        .then(async (response) => {
                            if (response.toLowerCase() === 'yes') {
                                await this.handleSaveOverrideDays();
                            }

                            if (response.toLowerCase() === 'no' && this.isEditOverrideDayMode) {
                                await this.getOverrideDays();
                            }

                            resolve();
                        });
                } else {
                    resolve();
                }
            });
        },
        getChangedRows() {
            const changedRows = this.overrideDayData.overrideDays.filter((overrideDay) => {
                if ([rowStatus.ADD, rowStatus.EDIT, rowStatus.DELETE].includes(overrideDay.rowStatus)) {
                    return true;
                }
                if (Array.isArray(overrideDay.overrideDayWindows)) {
                    return overrideDay.overrideDayWindows.some((window) =>
                        [rowStatus.ADD, rowStatus.EDIT, rowStatus.DELETE].includes(window.rowStatus)
                    );
                }
                return false;
            });

            const normalizedRows = changedRows.map((overrideDay) => {
                if (Array.isArray(overrideDay.overrideDayWindows)) {
                    overrideDay.overrideDayWindows = overrideDay.overrideDayWindows.map((window) => ({
                        ...window,
                        maxStops: window.maxStops === '' ? null : window.maxStops,
                        maxVolume: window.maxVolume === '' ? null : window.maxVolume,
                        maxWeight: window.maxWeight === '' ? null : window.maxWeight,
                        maxQuantity: window.maxQuantity === '' ? null : window.maxQuantity
                    }));
                }
                return overrideDay;
            });

            return normalizedRows;
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            const options = { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' };
            return new Intl.DateTimeFormat('en-US', options).format(date);
        },
        async handleSaveOverrideDays() {
            if (this.overrideDayDataIsValid()) {
                await this.saveOverrideDays();
            }
        },
        async handleBackToServiceAreas() {
            try {
                this.checkForUnsavedChanges().then(async () => {
                    if (!this.isEditOverrideDayMode) {
                        this.$router.push({ name: 'Service Areas' });
                    } else {
                        this.isEditOverrideDayMode = false;
                        this.resetToMainPage();
                    }
                });
            } catch (ex) {
                this.$notify({
                    message: ex.message,
                    type: 'danger'
                });
            }
        },
        async handleEditOverrideDay(overrideDay) {
            const missingFields = [];

            if (overrideDay.overrideDayName === null || overrideDay.overrideDayName === '') {
                missingFields.push('Name');
            }

            if (overrideDay.overrideDate === null) {
                missingFields.push('Override Date');
            }

            if (missingFields.length > 0) {
                showErrorMessage(
                    this,
                    `${missingFields.join(', ')} ${missingFields.length === 1 ? 'is' : 'are'} required.`
                );
                return;
            }

            if (this.hasOverrideDateDuplicates()) {
                showErrorMessage(this, 'Duplicate Override Date found.');
                return;
            }

            if (overrideDay.rowStatus === rowStatus.ADD) {
                overrideDay.overrideDayWindows = [];
                this.selectedOverrideDay = overrideDay;
                this.isEditOverrideDayMode = true;
                return;
            }

            if (
                overrideDay.rowStatus !== rowStatus.ADD &&
                (overrideDay.overrideDayWindows === undefined ||
                    overrideDay.overrideDayWindows === null ||
                    overrideDay.overrideDayWindows.length === 0)
            ) {
                await this.getOverrideDayWindows(overrideDay);
            } else {
                this.selectedOverrideDay = overrideDay;
                this.isEditOverrideDayMode = true;
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.card-icon {
    background-color: #384553 !important;
}

.custom-save-button,
.custom-back-button {
    min-width: 150px;
}

.custom-save-button {
    margin-right: 0;
}

.custom-back-button,
.custom-back-button:hover {
    background-color: #999999 !important;
    margin-left: 0;
}

.custom-override-day-toolbar {
    border-top: 1px solid #dddddd;
    padding-top: 10px !important;
    margin-top: 30px !important;
}

.override-day-page-title {
    font-weight: bold;
    font-size: 24px;
    margin-top: 20px;
    margin-bottom: 5px;
}

.override-day-crumb-title {
    font-weight: bold;
}
</style>
